import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { strapiGet, strapiPost, strapiPut, strapiDelete } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import type { Booking, BookingFilters, BookingStatus } from '@/lib/types/booking'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { isOrderBooking } from '@/lib/utils/bookingSemantics'

// ── Read hooks — served from Dexie (useLiveQuery) ───────────────────────────

function applyFilters(bookings: Booking[], filters: BookingFilters): Booking[] {
  let result = bookings

  if (filters.date)
    result = result.filter((b) => b.date === filters.date)

  if (filters.dateFrom || filters.dateTo) {
    if (filters.dateFrom) result = result.filter((b) => b.date >= filters.dateFrom!)
    if (filters.dateTo)   result = result.filter((b) => b.date <= filters.dateTo!)
  }

  if (filters.status?.length)
    result = result.filter((b) => filters.status!.includes(b.status))

  if (filters.bookingKind)
    result = result.filter((b) => (filters.bookingKind === 'order' ? isOrderBooking(b) : !isOrderBooking(b)))

  if (filters.source)
    result = result.filter((b) => b.source === filters.source)

  if (filters.excludeSource)
    result = result.filter((b) => b.source !== filters.excludeSource)

  if (filters.search) {
    const q = filters.search.toLowerCase()
    result = result.filter(
      (b) =>
        b.guest?.name?.toLowerCase().includes(q) ||
        b.guest?.email?.toLowerCase().includes(q),
    )
  }

  // Sort by dateTime ascending (same as server default)
  result = result.slice().sort((a, b) => a.dateTime.localeCompare(b.dateTime))

  // Client-side pagination
  const page = filters.page ?? 1
  const pageSize = filters.pageSize ?? 50
  const start = (page - 1) * pageSize
  return result.slice(start, start + pageSize)
}

export function useBookings(filters: BookingFilters) {
  // Always read ALL bookings with a simple querier — Dexie reliably tracks the
  // store this way. Conditional/chained queries can miss reactivity in Dexie 4.
  const allBookings = useLiveQuery(() => db.bookings.toArray(), [])

  const data = useMemo(() => {
    if (allBookings === undefined) return undefined
    return applyFilters(allBookings, filters)
  }, [
    allBookings,
    filters.date,
    filters.dateFrom,
    filters.dateTo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.status),
    filters.bookingKind,
    filters.source,
    filters.excludeSource,
    filters.search,
    filters.page,
    filters.pageSize,
  ])  // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data:
      data !== undefined
        ? { data, meta: { pagination: { total: data.length, page: filters.page ?? 1, pageSize: filters.pageSize ?? 50, pageCount: 1 } } }
        : undefined,
    isLoading: data === undefined,
  }
}

export function useBooking(documentId: string) {
  // Try Dexie first, fall back to network for bookings outside the synced window
  const local = useLiveQuery(
    () => (documentId ? db.bookings.get(documentId) : undefined),
    [documentId],
  )

  const networkQuery = useQuery({
    queryKey: ['booking', documentId],
    queryFn: () =>
      strapiGet<Booking>(`/api/bookings/${documentId}`, {
        populate: {
          guest: true,
          bookingTables: { populate: { table: true, areaSnapshot: true } },
          bookingCustomFields: { populate: { customField: true } },
          openingHour: true,
          comments: { sort: ['createdAt:asc'] },
          guestFeedback: true,
        },
      }),
    enabled: !!documentId && local === undefined,
    staleTime: 30_000,
  })

  if (local !== undefined) {
    return {
      data: { data: local },
      isLoading: false,
    }
  }
  return networkQuery
}

export function useTodayBookings() {
  const today = format(new Date(), 'yyyy-MM-dd')
  return useBookings({ date: today })
}

// ── Mutation hooks — write to Dexie + server ─────────────────────────────────

export function useCreateBooking() {
  return useMutation({
    mutationFn: async (data: Partial<Booking>) => {
      const restaurantId = useAppStore.getState().restaurantId
      const resp = await strapiPost<Booking>('/api/bookings', { data: { ...data, restaurant: restaurantId } })
      const booking = (resp as any).data ?? resp
      if (booking?.documentId) await db.bookings.put(booking)
      return resp
    },
    onSuccess: () => toast.success('Booking created'),
    onError: () => toast.error('Failed to create booking'),
  })
}

export function useUpdateBooking() {
  return useMutation({
    mutationFn: async ({ documentId, ...data }: { documentId: string } & Partial<Booking>) => {
      const resp = await strapiPut<Booking>(`/api/bookings/${documentId}`, { data })
      const booking = (resp as any).data ?? resp
      if (booking?.documentId) await db.bookings.put(booking)
      return resp
    },
    onSuccess: () => toast.success('Booking updated'),
    onError: () => toast.error('Failed to update booking'),
  })
}

export function useUpdateBookingStatus() {
  return useMutation({
    mutationFn: async ({ documentId, status }: { documentId: string; status: BookingStatus }) => {
      const existing = await db.bookings.get(documentId)
      const oldStatus = existing?.status
      // Optimistic: update Dexie immediately
      await db.bookings.update(documentId, { status })
      try {
        const resp = await strapiPut<Booking>(`/api/bookings/${documentId}`, { data: { status } })
        const booking = (resp as any).data ?? resp
        if (booking?.documentId) await db.bookings.put(booking)
        return resp
      } catch (err) {
        // Network failed — revert optimistic Dexie update
        if (oldStatus !== undefined) await db.bookings.update(documentId, { status: oldStatus })
        throw err
      }
    },
    onError: () => toast.error('Failed to update status'),
    onSuccess: () => toast.success('Status updated'),
  })
}

export function useDeleteBooking() {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const result = await strapiDelete(`/api/bookings/${documentId}`)
      await db.bookings.delete(documentId)
      return result
    },
    onSuccess: () => toast.success('Booking deleted'),
    onError: () => toast.error('Failed to delete booking'),
  })
}

// Returns a Set of table documentIds that are occupied during [time, time+duration) on date
export function useOccupiedTableIds(
  date: string,
  time: string,
  duration: number,
  excludeBookingDocId?: string,
) {
  const bookingsOnDate = useLiveQuery(
    () => date ? db.bookings.where('date').equals(date).toArray() : Promise.resolve([] as Booking[]),
    [date],
  )

  return useMemo(() => {
    const bookings = bookingsOnDate ?? []
    const filtered = bookings.filter((b) => b.status !== 'canceled' && b.status !== 'no_show')
    const [h, m] = time.split(':').map(Number)
    const newStart = h * 60 + m
    const newEnd = newStart + duration
    const occupied = new Set<string>()

    for (const b of filtered) {
      if (excludeBookingDocId && b.documentId === excludeBookingDocId) continue
      if (!b.time) continue
      const [bh, bm] = b.time.split(':').map(Number)
      const bStart = bh * 60 + bm
      const bEnd = bStart + (b.duration ?? 90)
      if (newStart < bEnd && newEnd > bStart) {
        for (const bt of b.bookingTables ?? []) {
          if (bt.table?.documentId) occupied.add(bt.table.documentId)
        }
      }
    }
    return occupied
  }, [bookingsOnDate, time, duration, excludeBookingDocId])
}
